import { Divider } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { isResponseWithData } from 'lib-shared-auth';
import { Presence } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { Media } from 'lib-shared-types-channels';

import { presenceApi } from 'apis/presence';
import {
  PresenceDropdownItem,
  PresenceToggle,
} from 'components/profile/components/PresenceDropdownItem';
import { useNotifications } from 'hooks/useNotifications';
import { getMetaWorkingMedia, mediaAttributes } from 'utils/channels/media';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

export type PresenceControlsProps = {
  user: {
    id: string;
    name: string | null;
  };
  presence: {
    status: Presence['status'];
    working_channels: Presence['working_channels'];
  };
};

export const PresenceControls = ({
  user,
  presence,
}: PresenceControlsProps): JSX.Element => {
  const flags = useFeatureFlag(['new-topbar']);
  const notify = useNotifications();
  const hasPresenceAccess = useHasAccess([
    'organization.member.presence.status',
  ]);

  const hasWorkingMediaAccess = useHasAccess([
    'organization.member.presence.working_media',
  ]);

  const [updateOtherPresence, updateOtherPresenceResult] =
    presenceApi.useUpdateOtherPresenceStatusMutation();

  const [updateOtherWorkingMedia, updateOtherWorkingMediaResult] =
    presenceApi.useUpdateOtherPresenceMediaMutation();

  const handleTogglePresence = async () => {
    const newStatus = presence.status === 'working' ? 'away' : 'working';
    const newStatusPretty = newStatus === 'working' ? 'Working' : 'Away';
    const response = await updateOtherPresence({
      agent_id: user.id,
      status: newStatus,
    });

    const agentName = user.name || 'agent';

    if (isResponseWithData(response)) {
      notify({
        title: `Status updated for ${agentName}`,
        text: `Status for ${agentName} updated to ${newStatusPretty}.`,
        type: 'success',
        icon: 'info',
        status: 'success',
        time: 5000,
      });
      ampli.presenceOtherAgentUpdated({ status: newStatus });
    } else {
      notify({
        title: `Failed to update status for ${agentName}`,
        text: `An error occurred while trying to set agent ${newStatusPretty}`,
        type: 'error',
        icon: 'exclamation',
        status: 'danger',
        time: 5000,
      });
    }
  };

  const handleWorkingMedia = async (media: Media, working: boolean) => {
    const response = await updateOtherWorkingMedia({
      agent_id: user.id,
      media,
      working,
    });

    const agentName = user.name || 'agent';

    if (isResponseWithData(response)) {
      notify({
        title: `Media updated for ${agentName}`,
        text: `${mediaAttributes[media].name} turned ${
          working ? 'on' : 'off'
        } for ${agentName}.`,
        type: 'success',
        icon: 'info',
        status: 'success',
        time: 5000,
      });
      ampli.presenceMediaOtherAgentUpdated({ enabled: working, media });
    } else {
      notify({
        title: `Failed to update ${mediaAttributes[media].name} for ${agentName}`,
        text: `An error occurred while trying to turn ${
          working ? 'on' : 'off'
        } ${mediaAttributes[media].name}`,
        type: 'error',
        icon: 'exclamation',
        status: 'danger',
        time: 5000,
      });
    }
  };

  const isAway = presence.status === 'away';
  const isOffline = presence.status === 'offline';
  const metaWorkingMedia = getMetaWorkingMedia(presence.working_channels);

  return (
    <>
      {hasPresenceAccess && (
        <PresenceDropdownItem
          key="working"
          icon="working"
          text="Away"
          disabled={isOffline}
        >
          <PresenceToggle
            active={isAway}
            isAway={isAway}
            disabled={isOffline || updateOtherPresenceResult.isLoading}
            onToggle={handleTogglePresence}
            id="working-other-toggle"
          />
        </PresenceDropdownItem>
      )}
      {hasPresenceAccess && hasWorkingMediaAccess && <Divider />}
      {hasWorkingMediaAccess &&
        metaWorkingMedia.map((media) => {
          const loading =
            updateOtherWorkingMediaResult.isLoading &&
            updateOtherWorkingMediaResult.originalArgs?.media === media.name;
          return (
            <PresenceDropdownItem
              key={media.name}
              icon={
                mediaAttributes[media.name][
                  flags.newTopbar ? 'icon' : 'legacyIcon'
                ]
              }
              text={mediaAttributes[media.name].name}
              disabled={isOffline}
            >
              <PresenceToggle
                active={media.working && !isOffline}
                isAway={isAway}
                disabled={isOffline || loading}
                onToggle={() => handleWorkingMedia(media.name, !media.working)}
                id={`${media.name}-other-toggle`}
              />
            </PresenceDropdownItem>
          );
        })}
    </>
  );
};
